import { ComponentProps, Dispatch, SetStateAction, useCallback, useMemo } from 'react';
import { Person } from '@weave/schema-gen-ts/dist/schemas/persons/v3/persons.pb';
import { ContactsTypes } from '@frontend/api-contacts';
import { PersonsV3 } from '@frontend/api-person';
import { SMSDataV3, SMSDataV4 } from '@frontend/api-sms-data';
import { ThreadContextAndOptions } from '@frontend/contact-actions-send-message';
import { useTranslation } from '@frontend/i18n';
import { useInboxNavigate } from '@frontend/inbox-navigation';
import { useAppFlagStore } from '@frontend/shared';
import { ThreadActions } from '@frontend/thread-actions';
import { ThreadHeader } from '@frontend/thread-header';
import { sentry } from '@frontend/tracking';
import { InboxPrefixes } from '@frontend/tracking-prefixes';
import { theme } from '@frontend/theme';
import { Button, useAlert, useMultiPanelStore } from '@frontend/design-system';
import { INBOX_PANEL_ID } from '../../../constants';
import { BulkSelectionActionType } from '../../../types';
import { BulkSelectionButtons } from './bulk-selection-buttons';
import { ResponsiveContactInfo } from './responsive-contact-info';
import { ResponsiveThreadActions } from './responsive-thread-actions';

type InboxThreadHeaderProps = {
  groupId: string;
  threadId: string;
  personPhone: string;
  contextPersonId?: string;
  departmentId?: string;
  isArchived: boolean;
  isBlocked: boolean;
  isAutomated: boolean;
  isNew: boolean;
  bulkSelectionType?: BulkSelectionActionType;
  setBulkSelectionType: (type?: BulkSelectionActionType) => void;
  canSmsWriteback: boolean;
  targetSmsData?: ThreadContextAndOptions['targetSmsData'];
  isChangingContextContact?: boolean;
  setIsChangingContextContact?: Dispatch<SetStateAction<boolean>>;
} & ComponentProps<typeof BulkSelectionButtons>;

export const InboxThreadHeader = ({
  groupId,
  threadId,
  personPhone,
  contextPersonId,
  departmentId,
  isArchived,
  isBlocked,
  isAutomated,
  isNew,
  bulkSelectionType,
  setBulkSelectionType,
  canSmsWriteback,
  targetSmsData,
  toggleSelectAll,
  allIsSelected,
  isChangingContextContact,
  setIsChangingContextContact,
}: InboxThreadHeaderProps) => {
  const { t } = useTranslation('inbox');
  const alert = useAlert();
  const { navigateToThread, updatePersonId, closeThread } = useInboxNavigate();
  const handleUpdateContextPersonId: typeof updatePersonId = useCallback(
    (...args) => {
      // Allow for drawer to close before updating the personId
      setTimeout(() => {
        updatePersonId(...args);
      });
    },
    [updatePersonId]
  );
  const { featureFlags } = useAppFlagStore();
  const { updateThreadsWithNewPerson } = SMSDataV4._QueryUpdaters.useQueryUpdaters();
  const { handlePersonPhoneSelection, handleDepartmentSelection } = SMSDataV3.Hooks.useThreadSelection({
    groupId,
    currentThreadId: threadId,
    selection: {
      personPhone,
      departmentId,
    },
    onSelectionChange: ({ threadId, newSelection, isNewThread }) => {
      navigateToThread({
        threadId,
        groupId,
        personPhone: newSelection.personPhone,
        departmentId: newSelection.departmentId,
        replace: true,
        isNew: isNewThread,
        personId: contextPersonId,
      });
    },
    onError: ({ err, selectionType, context }) => {
      alert.error(t('Error fetching selected thread.'));
      sentry.error({
        error: `Error looking up thread id from ${selectionType} selection`,
        topic: 'messages',
        addContext: {
          name: 'Selection context',
          context: {
            ...context,
            error: JSON.stringify(err),
          },
        },
      });
    },
  });

  const { panelInfo } = useMultiPanelStore(['panelInfo']);
  const inboxPanelIsHidden = panelInfo.find(({ id }) => id === INBOX_PANEL_ID)?.isHidden;
  const threadNamingIsEnabled = !!featureFlags[groupId]?.get('comm-platform-thread-naming')?.value;

  const {
    associatedContacts,
    primaryContactId,
    refetch: refetchAssociatedContacts,
  } = PersonsV3.PersonHooks.useAssociatedContacts({
    phoneNumber: personPhone,
    locationId: groupId,
    enabled: threadNamingIsEnabled,
  });
  const personId = threadNamingIsEnabled ? contextPersonId || primaryContactId : contextPersonId;
  const { primaryContact, contextContact } = useMemo<{ primaryContact?: Person; contextContact?: Person }>(
    () =>
      associatedContacts.reduce<{
        primaryContact?: Person;
        contextContact?: Person;
      }>((acc, curr) => {
        if (!acc.primaryContact && curr.personId === primaryContactId) acc.primaryContact = curr;
        if (!acc.contextContact && curr.personId === contextPersonId) acc.contextContact = curr;
        return acc;
      }, {}),
    [associatedContacts, contextPersonId, primaryContactId]
  );

  const { data: fetchedPerson } = PersonsV3.PersonQueries.useGetPersonLegacyQuery(
    {
      locationIds: [groupId],
      personId: personId ?? '',
    },
    {
      enabled: !!personId && !threadNamingIsEnabled,
    }
  );

  const onChangePrimaryContact = useCallback(
    async ({
      personPhone,
      ...rest
    }: {
      personPhone: string;
      personId: string;
      firstName: string;
      lastName: string;
      preferredName: string;
    }) => {
      updateThreadsWithNewPerson({
        personPhones: [personPhone],
        locationId: groupId,
        ...rest,
        onlyEmptyPersons: false,
      });
      refetchAssociatedContacts();
    },
    [groupId, refetchAssociatedContacts, updateThreadsWithNewPerson]
  );

  const handleContactCreation = useCallback(
    async (res: ContactsTypes.NewContactResponse, req: ContactsTypes.NewContactPayload) => {
      const allPersonPhones = [req.phone_mobile, req.phone_home].filter((phone) => !!phone);

      updateThreadsWithNewPerson({
        personPhones: allPersonPhones,
        firstName: req.first_name,
        lastName: req.last_name,
        preferredName: req.preferred_name ?? '',
        personId: res.data.person_id,
        locationId: groupId,
        onlyEmptyPersons: true,
      });
      refetchAssociatedContacts();
    },
    [groupId, refetchAssociatedContacts, updateThreadsWithNewPerson]
  );

  return (
    <ThreadHeader
      groupId={groupId}
      personPhone={personPhone ?? ''}
      outboundPhone={{ departmentId: departmentId ?? '' }}
      onPersonPhoneChange={(newPersonPhone) => handlePersonPhoneSelection(newPersonPhone, true)}
      onOutboundPhoneChange={({ departmentId }) => handleDepartmentSelection(departmentId, true)}
      variant={inboxPanelIsHidden ? 'responsive' : 'normal'}
      contextPersonId={contextPersonId}
      contextContact={contextContact}
      primaryContact={primaryContact}
      associatedContacts={threadNamingIsEnabled ? undefined : fetchedPerson ? [fetchedPerson] : []}
    >
      <ThreadHeader.Header css={{ minHeight: 72, padding: theme.spacing(2), columnGap: theme.spacing(2) }}>
        {!!inboxPanelIsHidden && (
          <Button
            iconName='back'
            variant='secondary'
            label={t('Back')}
            onClick={() => closeThread()}
            size='large'
            css={{ svg: { color: theme.colors.neutral70 } }}
          />
        )}
        <ThreadHeader.Title
          onCreateContact={handleContactCreation}
          onSecondaryPersonClick={() => {
            setIsChangingContextContact?.(false);
            handleUpdateContextPersonId('');
          }}
          css={{
            width: '100%',
          }}
        >
          <ResponsiveContactInfo threadNamingIsEnabled={threadNamingIsEnabled} />
        </ThreadHeader.Title>
        <ThreadHeader.AssociatedContactsDrawer
          key='associated-contacts-drawer'
          open={isChangingContextContact}
          onSelect={(personId) => {
            setIsChangingContextContact?.(false);
            handleUpdateContextPersonId(personId);
          }}
          onChangePrimaryContact={(args) => {
            onChangePrimaryContact({ ...args, personPhone });
            handleUpdateContextPersonId('');
          }}
          onClose={() => setIsChangingContextContact?.(false)}
        />
        <ThreadHeader.Drawer
          key='bulk-selection-drawer'
          open={!!bulkSelectionType}
          onClose={() => setBulkSelectionType(undefined)}
          hideCloseButton
          closeOnClickOutside={false}
        >
          <BulkSelectionButtons allIsSelected={allIsSelected} toggleSelectAll={toggleSelectAll} />
        </ThreadHeader.Drawer>
        <ThreadActions
          groupId={groupId}
          threadId={threadId}
          personPhone={personPhone}
          outboundPhone={{ departmentId: departmentId ?? '' }}
          personId={personId}
          person={contextContact || primaryContact}
          maxActions={2}
        >
          <ThreadActions.RequestPayment
            associatedPersonIds={associatedContacts?.map((contact) => contact.personId)}
            trackingIdSuffix={InboxPrefixes.Thread}
          />
          <ThreadActions.ContactViewOrCreate
            trackingId={`${InboxPrefixes.Thread}-${personId ? 'view' : 'create-contact'}-action-button`}
            onSave={handleContactCreation}
          />
        </ThreadActions>
      </ThreadHeader.Header>
      <ThreadHeader.SubHeader>
        <div
          css={{
            display: 'flex',
            gap: theme.spacing(1),
            alignItems: 'center',
            justifyContent: 'space-between',
            width: '100%',
          }}
        >
          <div
            css={{
              display: 'flex',
              gap: theme.spacing(1),
              alignItems: 'center',
              overflowX: 'hidden',
              minWidth: 0,
            }}
          >
            <ThreadHeader.PersonPhoneSelector
              css={{
                minWidth: 'fit-content',
                flexGrow: 2,
              }}
            />
            <ThreadHeader.DepartmentSelector css={{ flexGrow: 1, minWidth: theme.spacing(7) }} />
          </div>
          <ResponsiveThreadActions
            groupId={groupId}
            threadId={threadId}
            personPhone={personPhone}
            outboundPhone={{ departmentId: departmentId ?? '' }}
            personId={personId}
            person={contextContact || primaryContact}
            isArchived={isArchived}
            isAutomated={isAutomated}
            isBlocked={isBlocked}
            isNew={isNew}
            canSmsWriteback={canSmsWriteback}
            setBulkSelectionType={setBulkSelectionType}
            targetSmsData={targetSmsData}
          />
        </div>
      </ThreadHeader.SubHeader>
    </ThreadHeader>
  );
};
